import type { ComponentProps } from "react"
import Separator from "@/v2-ui/separator"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import { TooltipLink } from "@/v2-backoffice-client/components/link"
import { getLayerConsolePreviewUrl } from "@/v2-backoffice-client/layer/review/layer.review.utils"
import LayerReviewPublishButton from "@/v2-backoffice-client/layer/review/LayerReviewPublishButton"
import LayerReviewRejectButton from "@/v2-backoffice-client/layer/review/LayerReviewRejectButton"
import LayerReviewRequeueButton from "@/v2-backoffice-client/layer/review/LayerReviewRequeueButton"
import LayerDisableButton from "@/v2-backoffice-client/layer/enable/LayerDisableButton"
import LayerForkButton from "@/v2-backoffice-client/layer/fork/LayerForkButton"
import LayerAuthoringPromoteButton from "@/v2-backoffice-client/layer/authoring/LayerAuthoringPromoteButton"
import LayerAuthoringDeleteButton from "@/v2-backoffice-client/layer/authoring/LayerAuthoringDeleteButton"
import LayerAuthoringRestoreButton from "@/v2-backoffice-client/layer/authoring/LayerAuthoringRestoreButton"
import { useLayerAuthoringStatusMap } from "@/v2-backoffice-client/layer/authoring/layer.authoring.status.query"
import { PROD_STATE } from "@/v2-backoffice-client/layer/authoring/layer.authoring.constants"
import { IS_CATALOG_AUTHORING_ENABLED } from "@/v2-backoffice-client/feature/feature.constants"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"

export type LayerViewToolbarProps = ComponentProps<"div"> & {
  layer: LayerFragment | null | undefined
}

/**
 * Lifecycle controls for the layer sheet, mirroring the user sheet's
 * toolbar. Left half: the review lifecycle of this runtime (test, publish,
 * reject, re-queue, disable). Right half (dev backoffice only): the catalog
 * lifecycle — where the layer stands relative to production, push it there,
 * or delete/restore it from the catalog.
 *
 * Pure composition: every action button is its own component owning its
 * single mutation, confirm state, and error toast. The toolbar only decides
 * which buttons apply to this layer's current state.
 */
function LayerViewToolbar(props: LayerViewToolbarProps) {
  const { layer, ...viewSheetToolbarProps } = props

  // Catalog lifecycle (empty map on prod deployments, hiding the buttons).
  const statuses = useLayerAuthoringStatusMap()

  if (!layer) return null

  const isPending = !layer.IsEnabled && !layer.ReviewedAt
  const isRejected = !layer.IsEnabled && Boolean(layer.ReviewedAt)
  const status = statuses.get(layer.FeatureCode)

  return (
    <ViewSheet.Toolbar {...viewSheetToolbarProps}>
      <TooltipLink
        to={getLayerConsolePreviewUrl(layer.FeatureCode)}
        variant="ghost"
        isStacked
        iconSrc="map"
        content="Open the console with this layer force-loaded on the map — works while it is not live yet."
      >
        Preview
      </TooltipLink>

      {IS_CATALOG_AUTHORING_ENABLED && <LayerForkButton layer={layer} />}

      <Separator orientation="vertical" />

      {status &&
        !status.IsDeleted &&
        status.ProdState !== PROD_STATE.inProd && (
          <LayerAuthoringPromoteButton featureCode={layer.FeatureCode} />
        )}

      {!layer.IsEnabled && (
        <LayerReviewPublishButton
          layers={[layer]}
          isStacked
          size="md"
          iconSrc="eye"
        >
          Go live
        </LayerReviewPublishButton>
      )}

      {isPending && (
        <LayerReviewRejectButton
          layers={[layer]}
          isStacked
          size="md"
          iconSrc="eye-crossed"
        >
          Reject
        </LayerReviewRejectButton>
      )}

      {isRejected && (
        <LayerReviewRequeueButton
          layers={[layer]}
          isStacked
          size="md"
          iconSrc="rotate-left"
        >
          Re-queue
        </LayerReviewRequeueButton>
      )}

      {layer.IsEnabled && <LayerDisableButton layer={layer} />}

      {status && (
        <>
          <Separator orientation="vertical" />

          {status.IsDeleted ? (
            <LayerAuthoringRestoreButton featureCode={layer.FeatureCode} />
          ) : (
            <LayerAuthoringDeleteButton featureCode={layer.FeatureCode} />
          )}
        </>
      )}
    </ViewSheet.Toolbar>
  )
}

export default LayerViewToolbar
